import {
  CreateKnowledgeBaseSourceMaterialPayload,
  KnowledgeBaseClient,
  UpdateKnowledgeBaseSourceMaterialPayload,
} from 'api/clients/KnowledgeBaseClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { KnowledgeBaseSourceMaterial } from 'types'
import { buildErrorMessage } from 'utils'
import { reportError } from 'utils/error'

const allSourceMaterialsQueryKey = (workspaceId: number) => [workspaceId, 'sourceMaterials']

export const useSourceMaterials = () => {
  const workspaceId = useCurrentWorkspaceId()

  return useQuery({
    queryKey: allSourceMaterialsQueryKey(workspaceId),
    queryFn: async () => {
      const res = await KnowledgeBaseClient.getSourceMaterials()
      if (res.type === SUCCESS_RESPONSE_TYPE) return res.data.knowledge_base_source_materials
      throw new Error('Error fetching source materials')
    },
    onError: reportError,
    refetchOnWindowFocus: false,
  })
}

export const useUpdateSourceMaterial = () => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<KnowledgeBaseSourceMaterial, Error, UpdateKnowledgeBaseSourceMaterialPayload>(
    async (sourceMaterial: UpdateKnowledgeBaseSourceMaterialPayload) => {
      const response = await KnowledgeBaseClient.updateSourceMaterial(sourceMaterial)

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        reportError('Failed to update source material', {
          metaData: {
            sourceMaterial,
            response: response.data,
          },
        })
        if ('status' in response && response.status === 422) {
          throw new Error('Name has already been taken')
        } else {
          showToastMessage('We found an error updating the source material. Our team has been notified', 'error')
          throw new Error(buildErrorMessage(response.data.errors) || 'Error updating source material')
        }
      }
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(allSourceMaterialsQueryKey(workspaceId))
        showToastMessage('Source material updated')
      },
    },
  )
}

export const useDeleteSourceMaterial = (opts?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  return useMutation(
    async (sourceMaterialId: Parameters<(typeof KnowledgeBaseClient)['deleteSourceMaterial']>[0]) => {
      const res = await KnowledgeBaseClient.deleteSourceMaterial(sourceMaterialId)
      if (res.type !== SUCCESS_RESPONSE_TYPE) throw new Error('Error deleting source material')
      return res.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(allSourceMaterialsQueryKey(workspaceId))
        showToastMessage('Source material deleted')
        opts?.onSuccess?.()
      },
      onError: (error) => {
        reportError(error)
        showToastMessage('We found an error deleting the source material. Our team has been notified', 'error')
      },
    },
  )
}

export const useCreateSourceMaterial = () => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<KnowledgeBaseSourceMaterial, Error, CreateKnowledgeBaseSourceMaterialPayload>(
    async (sourceMaterial: CreateKnowledgeBaseSourceMaterialPayload) => {
      const response = await KnowledgeBaseClient.createSourceMaterial(sourceMaterial)

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        reportError('Failed to create source material', {
          metaData: {
            sourceMaterial,
            response: response.data,
          },
        })
        if ('status' in response && response.status === 422) {
          throw new Error('Name has already been taken')
        } else {
          showToastMessage('We found an error creating the source material. Our team has been notified', 'error')
          throw new Error(buildErrorMessage(response.data.errors) || 'Error creating source material')
        }
      }
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(allSourceMaterialsQueryKey(workspaceId))
        showToastMessage('Source material created')
      },
    },
  )
}
